import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { stockTransfersApi } from './api';
import type {
  CancelTransferPayload,
  ConfirmTransferPayload,
  CreateTransferPayload,
  RejectRemainingPayload,
  Transfer,
  TransferListParams,
  TransferListResponse,
  TransferStats,
  TransferStatsParams,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useStockTransfers(params: TransferListParams): UseQueryResult<TransferListResponse> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'stock-transfers',
      tenantId,
      params.page ?? 1,
      params.status ?? '',
      params.location_id ?? '',
      params.search ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
    ],
    queryFn: () => stockTransfersApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useStockTransferStats(
  params: TransferStatsParams,
): UseQueryResult<TransferStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['stock-transfers', tenantId, 'stats', params.location_id ?? ''],
    queryFn: () => stockTransfersApi.stats(params),
    staleTime: 15_000,
  });
}

export function useStockTransfer(id: string | undefined): UseQueryResult<Transfer | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['stock-transfers', tenantId, 'detail', id],
    queryFn: () => (id ? stockTransfersApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useCreateTransfer() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransferPayload) => stockTransfersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId] });
    },
  });
}

export function useDispatchTransfer() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => stockTransfersApi.dispatch(id),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId] });
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId, 'detail', vars.id] });
    },
  });
}

export function useConfirmTransfer() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ConfirmTransferPayload }) =>
      stockTransfersApi.confirm(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId] });
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId, 'detail', vars.id] });
    },
  });
}

export function useRejectRemainingTransfer() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectRemainingPayload }) =>
      stockTransfersApi.rejectRemaining(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId] });
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId, 'detail', vars.id] });
    },
  });
}

export function useCancelTransfer() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CancelTransferPayload }) =>
      stockTransfersApi.cancel(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId] });
      qc.invalidateQueries({ queryKey: ['stock-transfers', tenantId, 'detail', vars.id] });
    },
  });
}
